import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminChrome from '@/components/AdminChrome';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';

const REQUIRED = [
  { key: 'date_of_birth', label: 'Date of birth', placeholder: 'YYYY-MM-DD', hint: 'Used for payroll and legal HR records only.' },
  { key: 'address', label: 'Home address', placeholder: '123 Learn St, Denver CO 80202', hint: 'Full mailing address for W-2/1099 and equipment shipping.' },
  { key: 'phone', label: 'Phone number', placeholder: '+1 555 123 4567' },
  { key: 'emergency_contact_name', label: 'Emergency contact — name', placeholder: 'Alex Doe' },
  { key: 'emergency_contact_phone', label: 'Emergency contact — phone', placeholder: '+1 555 999 0000' },
];

const OPTIONAL = [
  { key: 'tax_id_last4', label: 'Tax ID (last 4 digits)', placeholder: '1234', maxLength: 4, hint: 'Never store your full SSN. Only the last 4 digits are collected.' },
  { key: 'bank_account_last4', label: 'Direct deposit — last 4', placeholder: '5678', maxLength: 4, hint: 'For payroll matching only. Full account is set up with our payroll provider.' },
  { key: 'shirt_size', label: 'Shirt size (for team swag)', placeholder: 'M' },
];

const MyHRProfilePage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [missing, setMissing] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/me/profile');
      setProfile(res.data.profile);
      setForm(res.data.profile || {});
      setMissing(res.data.required_missing || []);
      setCompleted(!!res.data.completed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load HR profile');
      navigate('/intern/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === '' || v === null || v === undefined) continue;
        payload[k] = v;
      }
      const res = await api.patch('/hr/me/profile', payload);
      if (res.data?.completed) {
        toast.success('HR profile complete — Day-1 task auto-marked done.');
      } else {
        toast.success('HR profile updated');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return (
      <AdminChrome activeSection="tasks">
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </AdminChrome>
    );
  }

  return (
    <AdminChrome activeSection="tasks">
      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(user?.is_admin ? '/admin' : '/intern/dashboard')} data-testid="hr-profile-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 mt-2" data-testid="hr-profile-title">Your HR profile</h1>
          <p className="text-sm text-slate-500 mt-1">
            Keep this information current. Payroll, tax forms, benefits, and emergency contact all use these fields.
          </p>
        </div>

        {/* Completion status card */}
        <Card
          className={completed
            ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50'
            : 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50'}
          data-testid="hr-status-card"
        >
          <CardContent className="p-5 flex items-start gap-4">
            {completed ? (
              <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h2 className="font-bold text-slate-900">
                {completed ? 'HR profile complete' : `${missing.length} required field${missing.length === 1 ? '' : 's'} remaining`}
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                {completed
                  ? 'Thanks! Your file is up to date. You can still edit anything below.'
                  : 'Fill in the required fields below to auto-complete your Day-1 "Complete HR profile" task.'}
              </p>
              {!completed && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {missing.map((m) => (
                    <Badge key={m} variant="outline" className="border-amber-400 text-amber-700 bg-white text-xs">
                      {REQUIRED.find((r) => r.key === m)?.label || m}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Required fields */}
        <Card data-testid="hr-required-card">
          <CardHeader>
            <CardTitle className="text-lg">Required information</CardTitle>
            <CardDescription>Needed before payroll can process your first paycheck.</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            {REQUIRED.map((f) => (
              <div key={f.key} className={f.key === 'address' ? 'md:col-span-2' : ''}>
                <Label>
                  {f.label} <span className="text-rose-500">*</span>
                </Label>
                <Input
                  value={form[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`hr-me-${f.key}`}
                />
                {f.hint && <p className="text-xs text-slate-500 mt-1">{f.hint}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Optional fields */}
        <Card data-testid="hr-optional-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Optional (paid roles)</CardTitle>
            <CardDescription>Only shown to platform admin & HR. Never used outside payroll and swag orders.</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            {OPTIONAL.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  value={form[f.key] || ''}
                  maxLength={f.maxLength}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`hr-me-${f.key}`}
                />
                {f.hint && <p className="text-xs text-slate-500 mt-1">{f.hint}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end sticky bottom-4 z-10">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 shadow-lg" data-testid="hr-me-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save profile
          </Button>
        </div>
      </div>
    </AdminChrome>
  );
};

export default MyHRProfilePage;
